import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ownerApi, type OwnerDecision } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import { OwnerSystemAuditView } from '@/features/owner/components/OwnerSystemAuditView';
import '../owner-decisions.css';

type Tab = 'decisions' | 'audit';

type DecisionItem = OwnerDecision;

function tabFromPath(pathname: string): Tab | null {
  if (pathname.endsWith('/decisions/decisions') || pathname.endsWith('/decisions/list')) {
    return 'decisions';
  }
  if (pathname.endsWith('/decisions/audit') || pathname.endsWith('/audit')) return 'audit';
  return null;
}

function statusLabel(status: string): string {
  if (status === 'ACTIVE') return 'Активно';
  if (status === 'SUPERSEDED') return 'Заменено';
  if (status === 'REPLACED') return 'Отменено';
  return status;
}

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

/** Owner decisions — template workflows decisions() + decision list markup. */
export const DecisionsAuditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useToast();
  const pathTab = tabFromPath(location.pathname);
  const [tab, setTab] = useState<Tab>(pathTab ?? 'decisions');
  const [decisions, setDecisions] = useState<DecisionItem[]>([]);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftReason, setDraftReason] = useState('');
  const [showComposer, setShowComposer] = useState(false);

  const loadDecisions = useCallback(async () => {
    try {
      const payload = await ownerApi.module('decisions', { tab: 'decisions' });
      const items = (payload.items as DecisionItem[] | undefined) ?? [];
      setDecisions(items);
    } catch {
      notify('Не удалось загрузить решения');
    }
  }, [notify]);

  useEffect(() => {
    const next = tabFromPath(location.pathname);
    if (next) setTab(next);
    else if (location.pathname.replace(/\/$/, '').endsWith('/decisions')) setTab('decisions');
  }, [location.pathname]);

  useEffect(() => {
    void loadDecisions();
  }, [loadDecisions]);

  const changeTab = (id: Tab) => {
    setTab(id);
    navigate(`${routes.owner}/decisions/${id}`);
  };

  const createDecision = async () => {
    if (!draftTitle.trim()) {
      notify('Укажите название решения');
      return;
    }
    try {
      await ownerApi.createDecision({
        title: draftTitle.trim(),
        summary: draftReason.trim() || 'Без дополнительного пояснения.',
      });
      setDraftTitle('');
      setDraftReason('');
      setShowComposer(false);
      notify('Новое решение создано');
      await loadDecisions();
      changeTab('decisions');
    } catch {
      notify('Не удалось создать решение');
    }
  };

  const revise = async (item: DecisionItem) => {
    try {
      await ownerApi.reviseDecision(item.id, {
        summary: `${item.summary} · новая версия`,
      });
      notify('Создана новая версия решения');
      await loadDecisions();
    } catch {
      notify('Не удалось создать новую версию');
    }
  };

  if (tab === 'audit') {
    return <OwnerSystemAuditView />;
  }

  const close = () => navigate(routes.owner);

  return (
    <div
      className="decision-log-overlay open"
      role="dialog"
      aria-modal="true"
      aria-label="Решения владельца"
    >
      <div className="decision-log-shell">
        <header className="decision-log-top">
          <button type="button" className="decision-log-btn" onClick={close}>
            ← Назад
          </button>
          <div>
            <h2>Решения владельца</h2>
            <small>Владелец системы · Zertteu Academy</small>
            <nav className="decision-log-nav" aria-label="Разделы">
              <button type="button" className="is-active" onClick={() => changeTab('decisions')}>
                Решения владельца
              </button>
              <button type="button" onClick={() => changeTab('audit')}>
                История системы
              </button>
            </nav>
          </div>
          <button type="button" className="decision-log-btn" onClick={close}>
            Закрыть ×
          </button>
        </header>

        <main className="decision-log-content">
          <section className="decision-log-hero" style={{ ['--a' as string]: '#e2ad3a' }}>
            <div>
              <h3>Решения владельца</h3>
              <p>
                Каждое изменение создаёт новую версию решения. Предыдущая версия сохраняется в
                истории как «Заменено» / «Отменено», а новая хранит связь «заменяет».
              </p>
            </div>
            <div className="decision-log-toolbar">
              <button
                type="button"
                className="decision-log-btn gold"
                onClick={() => setShowComposer((v) => !v)}
              >
                + Новое решение
              </button>
            </div>
          </section>

          {showComposer ? (
            <section className="decision-log-panel" style={{ marginBottom: 14 }}>
              <header>
                <div>
                  <h3>Новое решение</h3>
                  <small>Создание новой активной версии. Предыдущие активные переходят в «Заменено».</small>
                </div>
              </header>
              <div className="decision-log-body">
                <div className="decision-log-grid">
                  <label className="decision-log-field">
                    <span>Название</span>
                    <input
                      className="decision-log-input"
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      placeholder="Например: Маршрут заявок на 2026"
                    />
                  </label>
                  <label className="decision-log-field">
                    <span>Основание</span>
                    <textarea
                      className="decision-log-textarea"
                      value={draftReason}
                      onChange={(e) => setDraftReason(e.target.value)}
                      placeholder="Кратко опишите причину решения"
                    />
                  </label>
                </div>
                <div className="decision-log-toolbar">
                  <button
                    type="button"
                    className="decision-log-btn gold"
                    onClick={() => void createDecision()}
                  >
                    Сохранить решение
                  </button>
                  <button type="button" className="decision-log-btn" onClick={() => setShowComposer(false)}>
                    Отмена
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          <section className="decision-log-panel">
            <header>
              <div>
                <h3>История решений</h3>
                <small>
                  Активно — действующая версия · Заменено / Отменено — предыдущая сохранённая версия
                </small>
              </div>
            </header>
            <div className="decision-log-body decision-log-list">
              {decisions.length ? (
                decisions.map((item) => (
                  <article
                    key={`${item.id}-v${item.version}-${item.status}-${item.decided_at}`}
                    className="decision-log-item"
                  >
                    <div className="decision-log-copy">
                      <div className="decision-log-idline">
                        <strong>{item.id}</strong>
                        <span>Версия {item.version}</span>
                        <span
                          className={`decision-log-chip${item.status === 'ACTIVE' ? ' active' : ''}`}
                        >
                          {statusLabel(item.status)}
                        </span>
                      </div>
                      <h4>{item.title}</h4>
                      <p>{item.summary || 'Без дополнительного пояснения.'}</p>
                    </div>
                    <dl className="decision-log-meta">
                      <div>
                        <dt>Дата решения</dt>
                        <dd>{formatDate(item.decided_at)}</dd>
                      </div>
                      <div>
                        <dt>Автор</dt>
                        <dd>{item.author || 'Владелец системы'}</dd>
                      </div>
                      <div>
                        <dt>Заменяет</dt>
                        <dd>{item.replaces_id || 'Первая версия'}</dd>
                      </div>
                      <div>
                        <dt>Обновлено</dt>
                        <dd>{formatDate(item.updated_at || item.decided_at)}</dd>
                      </div>
                    </dl>
                    <div className="decision-log-actions">
                      {item.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          className="decision-log-btn"
                          onClick={() => void revise(item)}
                        >
                          Создать новую версию
                        </button>
                      ) : (
                        <span>Историческая версия</span>
                      )}
                    </div>
                  </article>
                ))
              ) : (
                <div className="owner-uat-empty">Решений пока нет.</div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};
